import { createSelector, createFeatureSelector, select } from '@ngrx/store';
import { pipe, distinctUntilChanged } from 'rxjs';

import { safeSelect, StateSelector } from '@fcom/core/selectors/selector-utils';
import { mapValues } from '@fcom/utils';
import { TripType } from '@fcom/core/constants';
import { GlobalBookingTravelClass, GlobalBookingTravelClassValue } from '@fcom/core';
import { LocationRouteType } from '@fcom/core-api/interfaces';
import { PaxAmount } from '@fcom/dapi/interfaces';

import { GlobalBookingTripDates } from '../../interfaces';
import {
  CommonState,
  CommonFeatureState,
  GlobalBookingState,
  TravelClassState,
  TravelClasses,
  LocationPair,
} from '../store.interface';
import { COMMON_FEATURE_KEY } from '../constants';

const _selectCommonState = createFeatureSelector<CommonFeatureState, CommonState>(COMMON_FEATURE_KEY);
const _selectGlobalBookingState = createSelector(_selectCommonState, (state: CommonState) => state.globalBooking);
const _getGlobalBookingDates = createSelector(
  _selectGlobalBookingState,
  (globalBookingState: GlobalBookingState) => globalBookingState.dates
);
const _getGlobalBookingTripType = createSelector(
  _selectGlobalBookingState,
  (globalBookingState: GlobalBookingState) => globalBookingState.tripType
);
const _getGlobalBookingTravelClass = createSelector(
  _selectGlobalBookingState,
  (globalBookingState: GlobalBookingState) => globalBookingState.travelClasses.selected
);
const _globalBookingTravelClassState = createSelector(
  _selectGlobalBookingState,
  (state: GlobalBookingState) => state.travelClasses
);
const _globalBookingTravelClasses = createSelector(
  _globalBookingTravelClassState,
  (tcs: TravelClassState): TravelClasses =>
    tcs
      ? mapValues(tcs.availability, (available: boolean, key: GlobalBookingTravelClassValue) => ({
          available,
          selected: tcs.selected === key,
        }))
      : undefined
);
const _getGlobalBookingOrigin = createSelector(
  _selectGlobalBookingState,
  (globalBookingState: GlobalBookingState) => globalBookingState.origin
);

const _getGlobalBookingRouteType = createSelector(
  _selectGlobalBookingState,
  (globalBookingState: GlobalBookingState) => globalBookingState.routeType
);

const _getGlobalBookingDiscountCode = createSelector(
  _selectGlobalBookingState,
  (globalBookingState: GlobalBookingState) => globalBookingState.discountCode
);

const _globalBookingLocations = createSelector(
  _selectGlobalBookingState,
  (globalBookingState: GlobalBookingState) => globalBookingState.locations
);

export const _globalBookingPaxAmount = createSelector(
  _selectGlobalBookingState,
  (globalBookingState) => globalBookingState?.paxAmount
);

export const globalBookingDates = (): StateSelector<CommonFeatureState, GlobalBookingTripDates> =>
  pipe(
    select(_getGlobalBookingDates),
    distinctUntilChanged((prev, next) => {
      // TODO: (booking-widget migration) add a test for this
      return prev.departureDate === next.departureDate && prev.returnDate === next.returnDate;
    })
  );
export const globalBookingTripType = (): StateSelector<CommonFeatureState, TripType> =>
  select(_getGlobalBookingTripType);
export const globalBookingTravelClass = (): StateSelector<CommonFeatureState, GlobalBookingTravelClass> =>
  select(_getGlobalBookingTravelClass);
export const globalBookingTravelClasses = (): StateSelector<CommonFeatureState, TravelClasses> =>
  select(_globalBookingTravelClasses);
export const globalBookingSelections = (): StateSelector<CommonFeatureState, GlobalBookingState> =>
  select(_selectGlobalBookingState);
export const globalBookingOrigin = (): StateSelector<CommonFeatureState, string> => select(_getGlobalBookingOrigin);
export const globalBookingRouteType = (): StateSelector<CommonFeatureState, LocationRouteType> =>
  select(_getGlobalBookingRouteType);
export const globalBookingDiscountCode = (): StateSelector<CommonFeatureState, string> =>
  select(_getGlobalBookingDiscountCode);
export const globalBookingLocations = (): StateSelector<CommonFeatureState, LocationPair[]> =>
  select(_globalBookingLocations);
export const globalBookingPaxAmount = (): StateSelector<CommonFeatureState, PaxAmount> =>
  safeSelect(_globalBookingPaxAmount);
